import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { ZAPIER_INVITE_URL } from "../../../constants";
import OptionToggler from "../../../parts/OptionToggler";
import SvgIcon from "../../../parts/SvgIcon";
import CopyField from "./components/CopyField";

const WEBHOOK_URL = "https://app.mystrengthbook.com/api/v1/zapier/pull";

const PullPreferences = ({
  isFe,
  isCoach,
  referAsClients,
  chatEnabled,
  userToSendInformation,
  roleName,
  coachId,
  zapierApiKey,
  canGenerateKey,
  onFormChange,
  inAppCommunityEnabled,
  schedulingEnabled,
}) => {
  const { t } = useTranslation(["dashboard", "common"]);
  const referAsClientsOptions = [
    { label: t("pullPreferences.athlete"), value: false },
    { label: t("pullPreferences.client"), value: true },
  ];
  const chatEnabledOptions = [
    { label: t("common:yes"), value: true },
    { label: t("common:no"), value: false },
  ];
  const schedulingOptions = [
    { label: t("pushPressCommunityCardActions.on"), value: true },
    { label: t("pushPressCommunityCardActions.off"), value: false },
  ];

  return (
    <>
      {!!(isFe || isCoach) && (
        <form id="form-pull" method="POST" data-noreset onChange={onFormChange}>
          <input
            type="hidden"
            name="userToSendInformation"
            defaultValue={userToSendInformation}
          />
          <input type="hidden" name="roleName" defaultValue={roleName}></input>
          <div className="row">
            <p className="mobile-hidden">
              {t("pullPreferences.userReference")}
            </p>
            <div className="only-show-mobile">
              <p>{t("pullPreferences.userReference")}</p>
            </div>
            <OptionToggler
              name="referAsClients"
              options={referAsClientsOptions}
              defaultValue={referAsClients}
            />
          </div>
          <div className="row" data-col="1/1">
            <p className="mobile-hidden">{t("pullPreferences.chatEnabled")}</p>
            <div className="only-show-mobile">
              <p>{t("pullPreferences.chatEnabled")}</p>
            </div>
            <OptionToggler
              name="chatEnabled"
              options={chatEnabledOptions}
              defaultValue={chatEnabled}
            />
          </div>
          <div className="row" data-col="1/1">
            <p className="mobile-hidden">
              {t("pullPreferences.communityChat")}
            </p>
            <div className="only-show-mobile">
              <p>{t("pullPreferences.communityChat")}</p>
            </div>
            <OptionToggler
              name="inAppCommunityEnabled"
              options={chatEnabledOptions}
              defaultValue={inAppCommunityEnabled}
            />
          </div>

          <h4 style={{ margin: "1.6rem 0 1.2rem" }}>
            {t("pullPreferences.scheduling")}
          </h4>
          <div className="row">
            <p className="mobile-hidden">
              {t("pullPreferences.schedulingEnabled")}
            </p>
            <div className="only-show-mobile">
              <p>{t("pullPreferences.schedulingEnabled")}</p>
            </div>
            <OptionToggler
              name="schedulingEnabled"
              options={schedulingOptions}
              defaultValue={schedulingEnabled}
            />
          </div>

          <div className="flex justify-between items-center mb-[1.2rem] mt-[1.6rem]">
            <h4>{t("pullPreferences.zapierIntegration")}</h4>
            <div
              className={`nav-status-badge ${zapierApiKey ? "active" : "inactive"}`}
            >
              {zapierApiKey
                ? t("settingsNav.productActive")
                : t("settingsNav.productInactive")}
            </div>
          </div>
          <p className="p2 -mt-2">{t("pullPreferences.zapierIntro")}</p>

          <h5 style={{ marginBottom: "0.75rem", marginTop: "1.5rem" }}>
            {t("pullPreferences.zapierExampleAutomations")}
          </h5>

          <ul className="pl-4 flex flex-col gap-2 mb-4">
            <li className="list-disc">
              <p className="p2">{t("pullPreferences.zapierExample1")}</p>
            </li>
            <li className="list-disc">
              <p className="p2">{t("pullPreferences.zapierExample2")}</p>
            </li>
            <li className="list-disc">
              <p className="p2">{t("pullPreferences.zapierExample3")}</p>
            </li>
            <li className="list-disc">
              <p className="p2">{t("pullPreferences.zapierExample4")}</p>
            </li>
          </ul>

          {zapierApiKey ? (
            <>
              <CopyField label={t("pullPreferences.coachId")} value={coachId} />
              <CopyField
                label={t("pullPreferences.apiKey")}
                value={zapierApiKey}
              />
              <div className="flex gap-2 mt-4 zapier-actions">
                <a
                  href={ZAPIER_INVITE_URL}
                  role="button"
                  data-button-type="primary"
                  data-icon
                  target="_blank"
                  rel="noreferrer"
                >
                  <SvgIcon src="/img/new-icons/lightning.svg" />
                  {t("pullPreferences.connectZapierApp")}
                  <SvgIcon src="/img/new-icons/export.svg" />
                </a>
                <a
                  href="https://msbapp.notion.site/Setting-Up-the-Zapier-Integration-for-PULL-1-1-Coaching-367e68dcd77680cb91facff1270fbbc9"
                  role="button"
                  data-button-type="tertiary"
                  data-icon
                  target="_blank"
                  rel="noreferrer"
                >
                  <SvgIcon src="/img/new-icons/book.svg" />
                  {t("pullPreferences.viewSetupGuide")}
                  <SvgIcon src="/img/new-icons/export.svg" />
                </a>
              </div>
              <details className="mt-4">
                <summary className="p2">
                  {t("pullPreferences.legacyWebhookSetup")}
                </summary>
                <CopyField
                  label={t("pullPreferences.webhookUrl")}
                  value={WEBHOOK_URL}
                />
              </details>
            </>
          ) : (
            <div className="row">
              <p style={{ opacity: 0.6 }}>
                {canGenerateKey
                  ? t("pullPreferences.zapierCredentialsHint")
                  : t("pullPreferences.zapierCredentialsHintAdmin")}
              </p>
              {canGenerateKey && (
                <button
                  type="button"
                  data-button-type="primary"
                  onClick={() =>
                    submit("/actions/coach/generate-zapier-key", {
                      method: "POST",
                    })
                  }
                >
                  {t("pullPreferences.generateZapierKey")}
                </button>
              )}
            </div>
          )}
        </form>
      )}
    </>
  );
};
export default PullPreferences;
